import {
  Button,
  Grid,
  MenuItem,
  styled,
  CircularProgress,
} from '@mui/material';
import AppModal from 'components/AppModal';
import FlexBox from 'components/flexbox/FlexBox';
import AppTextField from 'components/input-fields/AppTextField';
import CalendarInput from 'components/input-fields/CalendarInput';
import { H2, H6 } from 'components/Typography';
import { useFormik } from 'formik';
import { type FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { courseTypesApi, employeeCertificationsApi } from '../../api';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { CloudUpload } from '@mui/icons-material';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  employeeId: number;
  edit?: boolean;
  data?: any;
}

const StyledAppModal = styled(AppModal)(({ theme }) => ({
  maxWidth: 600,
  minWidth: 200,
  [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

const AddCertificationModal: FC<ModalProps> = ({
  open,
  onClose,
  employeeId,
  edit,
  data,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [courseTypes, setCourseTypes] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    courseTypesApi.findAll().then(({ data }) => {
      setCourseTypes(data.filter((ct: any) => ct.isActive));
    });
  }, []);

  const initialValues = {
    courseTypeId: data?.courseTypeId || '',
    obtainedDate: data?.obtainedDate ? new Date(data.obtainedDate) : null as Date | null,
    expirationDate: data?.expirationDate ? new Date(data.expirationDate) : null as Date | null,
    certificateNumber: data?.certificateNumber || '',
    issuingAuthority: data?.issuingAuthority || '',
    notes: data?.notes || '',
  };

  const validationSchema = Yup.object().shape({
    courseTypeId: Yup.number().required(
      t('common.forms.field.required', {
        field: t('training.courseType.itemName'),
      }),
    ),
    obtainedDate: Yup.date()
      .nullable()
      .required(
        t('common.forms.field.required', {
          field: t('training.certifications.obtainedDate'),
        }),
      ),
    expirationDate: Yup.date().nullable(),
    certificateNumber: Yup.string().nullable(),
    issuingAuthority: Yup.string().nullable(),
    notes: Yup.string().nullable(),
  });

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    touched,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (formValues) => {
      const payload = {
        employeeId,
        courseTypeId: Number(formValues.courseTypeId),
        obtainedDate: formValues.obtainedDate
          ? formValues.obtainedDate.toISOString().split('T')[0]
          : '',
        expirationDate: formValues.expirationDate
          ? formValues.expirationDate.toISOString().split('T')[0]
          : undefined,
        certificateNumber: formValues.certificateNumber || undefined,
        issuingAuthority: formValues.issuingAuthority || undefined,
        notes: formValues.notes || undefined,
      };

      try {
        let certId: number;

        if (edit && data?.id) {
          const { employeeId: _eid, ...updatePayload } = payload;
          await employeeCertificationsApi.update(String(data.id), updatePayload);
          certId = data.id;
          toast.success(t('training.certifications.updated'));
        } else {
          const res = await employeeCertificationsApi.create(payload);
          certId = res.data.id;
          toast.success(t('training.certifications.created'));
        }

        if (selectedFile) {
          await uploadFile(certId);
        }

        queryClient.invalidateQueries({ queryKey: ['employeeCertifications'] });
        queryClient.invalidateQueries({
          queryKey: ['trainingCompliance'],
        });
        onClose();
        resetForm();
        setSelectedFile(null);
      } catch {
        toast.error(t('common.error'));
      }
    },
  });

  const uploadFile = async (certId: number) => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('files', selectedFile);
      await employeeCertificationsApi.createFile(String(certId), {
        data: formData,
      });
      toast.success(t('training.certifications.fileUploaded'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <StyledAppModal
      open={open}
      handleClose={() => {
        onClose();
        resetForm();
        setSelectedFile(null);
      }}
    >
      <H2 mb={2}>
        {edit
          ? t('common.forms.button.edit') +
            ' ' +
            t('training.certifications.title')
          : t('training.certifications.addButton')}
      </H2>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <H6 mb={1}>{t('training.courseType.itemName')}</H6>
            <AppTextField
              select
              fullWidth
              size="small"
              name="courseTypeId"
              value={values.courseTypeId}
              onChange={handleChange}
              error={Boolean(errors.courseTypeId && touched.courseTypeId)}
              helperText={
                (touched.courseTypeId && errors.courseTypeId) as string
              }
              disabled={edit}
            >
              {courseTypes.map((ct) => (
                <MenuItem key={ct.id} value={ct.id}>
                  {ct.name} ({ct.category})
                </MenuItem>
              ))}
            </AppTextField>
          </Grid>

          <Grid item xs={6}>
            <H6 mb={1}>{t('training.certifications.obtainedDate')}</H6>
            <CalendarInput
              format="dd/MM/yyyy"
              label={t('training.certifications.obtainedDate')}
              value={values.obtainedDate}
              onChange={async (date) => setFieldValue('obtainedDate', date)}
              slotProps={{
                textField: {
                  size: 'small',
                  error: Boolean(errors.obtainedDate && touched.obtainedDate),
                  helperText: (touched.obtainedDate && errors.obtainedDate) as string,
                },
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <H6 mb={1}>{t('training.certifications.expirationDate')}</H6>
            <CalendarInput
              format="dd/MM/yyyy"
              label={t('training.certifications.expirationDate')}
              value={values.expirationDate}
              onChange={async (date) => setFieldValue('expirationDate', date)}
              slotProps={{
                textField: {
                  size: 'small',
                  error: Boolean(errors.expirationDate && touched.expirationDate),
                  helperText: (touched.expirationDate && errors.expirationDate) as string,
                },
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <H6 mb={1}>{t('training.certifications.certificateNumber')}</H6>
            <AppTextField
              fullWidth
              size="small"
              name="certificateNumber"
              placeholder={t('training.certifications.certificateNumber')}
              value={values.certificateNumber}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <H6 mb={1}>{t('training.certifications.issuingAuthority')}</H6>
            <AppTextField
              fullWidth
              size="small"
              name="issuingAuthority"
              placeholder={t('training.certifications.issuingAuthority')}
              value={values.issuingAuthority}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <H6 mb={1}>{t('training.certifications.notes')}</H6>
            <AppTextField
              fullWidth
              size="small"
              name="notes"
              multiline
              rows={2}
              placeholder={t('training.certifications.notes')}
              value={values.notes}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <H6 mb={1}>{t('training.certifications.uploadFile')}</H6>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              size="small"
              fullWidth
            >
              {selectedFile
                ? selectedFile.name
                : t('training.certifications.uploadFile')}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setSelectedFile(file);
                }}
              />
            </Button>
          </Grid>
        </Grid>

        <FlexBox justifyContent="flex-end" gap={2} marginTop={3}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              onClose();
              resetForm();
              setSelectedFile(null);
            }}
          >
            {t('common.forms.button.cancel')}
          </Button>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={uploading}
            startIcon={
              uploading ? <CircularProgress size={16} /> : undefined
            }
          >
            {edit
              ? t('common.forms.button.save')
              : t('training.certifications.addButton')}
          </Button>
        </FlexBox>
      </form>
    </StyledAppModal>
  );
};

export default AddCertificationModal;
